import { useEffect, useRef, useState } from "react";

type IntersectionObserverResult = [
  (node: Element | null) => void,
  IntersectionObserverEntry | undefined,
];

/**
 * Observe a node's viewport intersection via a callback ref.
 *
 * The observer's whole lifecycle lives inside one effect, keyed on the node.
 * The previous version observed in the ref callback but disconnected in an
 * effect cleanup -- and React 18's StrictMode replays effect pairs without
 * re-firing callback refs, so in development every observer ended up
 * permanently disconnected after the replay: scroll-revealed sections never
 * mounted, viewport-triggered videos never played, infinite scroll never
 * loaded. Production, which does not replay effects, was unaffected.
 */
export const useIntersect = (
  options?: IntersectionObserverInit,
): IntersectionObserverResult => {
  const [node, setNode] = useState<Element | null>(null);
  const [entry, setEntry] = useState<IntersectionObserverEntry>();
  // Captured once, matching the old hook's behaviour: no caller passes
  // options today, and a fresh inline object each render must not tear the
  // observer down.
  const optionsRef = useRef(options);

  useEffect(() => {
    if (!node) {
      return undefined;
    }

    const observer = new IntersectionObserver(([observedEntry]) => {
      setEntry(observedEntry);
    }, optionsRef.current);
    observer.observe(node);

    return () => observer.disconnect();
  }, [node]);

  return [setNode, entry];
};
